import { WifiOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatTime } from "../lib/locale.ts";
import { getLastSync } from "../lib/offline-cache.ts";

/**
 * Small banner shown when the device loses connectivity, so staff on unstable
 * clinic 4G know they may be looking at stale cached data (the PWA serves a
 * cache fallback). Hidden while online. Sits above the bottom tab bar.
 */
export function OfflineIndicator() {
	const { t } = useTranslation("common");
	const [offline, setOffline] = useState(
		typeof navigator !== "undefined" && !navigator.onLine,
	);

	useEffect(() => {
		const goOffline = () => setOffline(true);
		const goOnline = () => setOffline(false);
		window.addEventListener("offline", goOffline);
		window.addEventListener("online", goOnline);
		return () => {
			window.removeEventListener("offline", goOffline);
			window.removeEventListener("online", goOnline);
		};
	}, []);

	if (!offline) return null;

	// Reassure staff how recent the offline (cached) data is.
	const synced = getLastSync();
	const syncedLabel = synced ? formatTime(synced) : null;

	return (
		<output className="fixed inset-x-0 z-40 flex justify-center px-4 bottom-[calc(3.25rem+env(safe-area-inset-bottom)+0.5rem)] sm:bottom-4 pointer-events-none">
			<div className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-gray-900/90 dark:bg-gray-100/90 text-white dark:text-gray-900 text-xs font-medium px-3 py-2 shadow-lg backdrop-blur">
				<WifiOff className="size-3.5" strokeWidth={2.5} aria-hidden />
				<span>
					{t("offline.banner", {
						defaultValue: "لا يوجد اتصال — تُعرض بيانات محفوظة",
					})}
				</span>
				{syncedLabel && (
					<span className="opacity-70" dir="ltr">
						·{" "}
						{t("offline.synced_at", {
							time: syncedLabel,
							defaultValue: "آخر مزامنة {{time}}",
						})}
					</span>
				)}
			</div>
		</output>
	);
}
